/**
 * Pre-rendered SFX bank (MOBILE_PLAN §6 "ZzFX bank"): the whole synth bank rendered ONCE at asset-build time into one
 * mono 16-bit WAV plus an index, instead of synthesising ~32 s of audio in JS at runtime. On the web the bank renders
 * in a Worker; React Native has no Worker and no requestIdleCallback, so the runtime path costs seconds of JS-thread
 * time on a low-tier phone. The native shell decodes the WAV with its audio context (one native decode), slices it
 * with `sliceBakedBank` and hands the samples to the engine (`AudioEngine.addRendered`, with `prerender: false`).
 *
 *   build: apps/client `pnpm assets --target mobile` → apps/mobile/assets/generated/sfx-bank.{wav,json}
 *
 * Pure (no WebAudio): runs in Node (the bake), Vitest and Hermes.
 */
import { renderSfx } from './render';
import { SFX_IDS, sfxDef } from './sfx-bank';

/** The bank's index: sample rate of the bake and, per sound, [first sample, sample count] in the WAV. */
export interface BakedSfxIndex {
  version: 1;
  rate: number;
  /** Total samples in the WAV. */
  samples: number;
  sounds: Record<string, readonly [start: number, length: number]>;
}

export interface RenderedSound {
  data: Float32Array;
  rate: number;
}

/** Render `ids` at `rate` and lay them end to end (index + concatenated samples). */
export const bakeSfxBank = (
  ids: readonly string[] = SFX_IDS,
  rate = 48000,
): { index: BakedSfxIndex; samples: Float32Array } => {
  const parts: [string, Float32Array][] = [];
  let total = 0;
  for (const id of ids) {
    const def = sfxDef(id);
    if (!def) continue;
    const data = renderSfx(def, rate);
    parts.push([id, data]);
    total += data.length;
  }
  const samples = new Float32Array(total);
  const sounds: BakedSfxIndex['sounds'] = {};
  let at = 0;
  for (const [id, data] of parts) {
    samples.set(data, at);
    sounds[id] = [at, data.length];
    at += data.length;
  }
  return { index: { version: 1, rate, samples: total, sounds }, samples };
};

/** Mono 16-bit PCM WAV bytes of `samples` (clamped to ±1). */
export const encodeWav16 = (samples: Float32Array, rate: number): Uint8Array => {
  const bytes = new Uint8Array(44 + samples.length * 2);
  const v = new DataView(bytes.buffer);
  const str = (o: number, s: string): void => {
    for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i);
  };
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples.length * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    v.setInt16(44 + i * 2, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true);
  }
  return bytes;
};

/**
 * Cut the decoded bank (`channel`: channel 0 of the decoded WAV, at `channelRate` — a decoder may resample to its
 * context's rate) back into per-sound samples, keyed by SFX id.
 */
export const sliceBakedBank = (
  channel: Float32Array,
  channelRate: number,
  index: BakedSfxIndex,
): Map<string, RenderedSound> => {
  const k = channelRate / index.rate;
  const out = new Map<string, RenderedSound>();
  for (const [id, [start, length]] of Object.entries(index.sounds)) {
    const a = Math.round(start * k);
    const b = Math.min(channel.length, Math.round((start + length) * k));
    if (b <= a) continue;
    out.set(id, { data: channel.slice(a, b), rate: channelRate });
  }
  return out;
};

/**
 * Linear-interpolation resample of mono samples (`from` Hz → `to` Hz). For engines that play every buffer at the
 * context's rate whatever its own `sampleRate` (react-native-audio-api 0.13: a 48 kHz sound in a 44.1 kHz context
 * would play 9 % slow and flat). Enough for the bank's short synth sounds; files decode at the context rate.
 */
export const resampleLinear = (data: Float32Array, from: number, to: number): Float32Array => {
  if (!(from > 0) || !(to > 0) || from === to || data.length === 0) return data;
  const n = Math.max(1, Math.round((data.length * to) / from));
  const out = new Float32Array(n);
  const step = from / to;
  const last = data.length - 1;
  for (let i = 0; i < n; i++) {
    const x = i * step;
    const j = Math.floor(x);
    if (j >= last) {
      out[i] = data[last]!;
      continue;
    }
    const f = x - j;
    out[i] = data[j]! + (data[j + 1]! - data[j]!) * f;
  }
  return out;
};
